import React, { useEffect, useState } from 'react';
import { Redirect } from 'react-router';
import { useI18nPath } from '../../../config/clientRoutes/hooks';
import { useUser } from '../../shared/hooks';
import { StudentAssignments, UnfinishedTrajectories } from '../components';
import DashboardItem from '../components/DashboardItem';
import { useTranslation } from 'react-i18next';
import { useMediaQuery, styled, Theme, CircularProgress } from '@material-ui/core';
import { LogActionTypes, useDataLogging } from '../../shared/data-logging';
import { useAppDispatch } from 'src/modules/shared/hooks/useAppDispatch';
import { setShowFilters } from 'src/modules/shared/slices/filters/entity/FiltersSlice';
import ArchiveList from '../../book/components/ArchiveList';
import { useGetArchiveQuery } from 'src/modules/shared/slices/archive/api/archive';
import { Message } from 'src/modules/shared/components';
import { setPageWidth } from 'src/modules/shared/slices/privatePageSettings/entity/privatePageSettingsSlice';

const ContentWrapper = styled('div')<Theme>(({ theme }) => ({
  marginBottom: theme.spacing(3),
  height: 'auto',
  position: 'relative',
  display: 'flex',
  gap: theme.spacing(3),

  '& > *': {
    flex: 1,
  },
  '&.desktop-50': {
    width: 'calc(50% - 0.5rem)',
  },
  [theme.breakpoints.down('sm')]: {
    display: 'block',
    '&.desktop-50': {
      width: '100%',
    },
  },
}));

const TabsWrapper = styled('div')<Theme>(({ theme }) => ({
  position: 'fixed',
  top: 'auto',
  bottom: 0,
  left: 0,
  width: '100%',
  height: '6rem',
  background: theme.palette.common.white,
  zIndex: 999,
  transform: 'none',
  display: 'flex',
}));

const Tab = styled('button')<Theme>(({ theme }) => ({
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '50%',
  maxWidth: '50%',
  fontSize: theme.typography.bodyMd.fontSize,
  fontFamily: theme.config.secondaryFontFamily,
  textTransform: 'uppercase',
  color: theme.palette.grey['900'],
  background: theme.palette.common.white,
  '&.active': {
    color: theme.palette.common.white,
    background: theme.palette.primary.main,
  },

  '&:first-child': {
    borderRight: theme.config.defaultBorder(theme.palette.grey['200']),
  },
}));

const StudentDashboardPage = () => {
  // hooks
  const { getPath } = useI18nPath();
  const { isStudent } = useUser();
  const { t } = useTranslation();
  const log = useDataLogging();
  const {
    data: archiveData,
    isLoading: archiveLoading,
    isError: archiveError,
    isSuccess: archiveSuccess,
  } = useGetArchiveQuery();
  const dispatch = useAppDispatch();

  // mediaquery
  const isMobile = useMediaQuery('(max-width: 740px)');

  // state
  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    dispatch(setShowFilters(false));
  }, [dispatch]);

  useEffect(() => {
    dispatch(setPageWidth(100));
  }, [dispatch]);

  useEffect(() => {
    log(LogActionTypes.NAVIGATION_HOMEPAGE);
  }, [log]);

  if (!isStudent) return <Redirect to={getPath('books')} />;

  return (
    <>
      {isMobile && (
        <TabsWrapper>
          <Tab
            className={selectedTab === 0 ? 'active' : undefined}
            onClick={() => setSelectedTab(0)}
          >
            Mijn lesmethodes
          </Tab>
          <Tab
            className={selectedTab === 1 ? 'active' : undefined}
            onClick={() => setSelectedTab(1)}
          >
            {t('studentDashboard.assignments.title')}
          </Tab>
        </TabsWrapper>
      )}
      <ContentWrapper>
        {(!isMobile || (isMobile && selectedTab === 0)) && (
          <DashboardItem title="Mijn lesmethodes">
            <UnfinishedTrajectories style={{ paddingTop: '1rem' }} />
          </DashboardItem>
        )}
        {(!isMobile || (isMobile && selectedTab === 1)) && (
          <DashboardItem title={t('studentDashboard.assignments.title')}>
            <StudentAssignments />
          </DashboardItem>
        )}
      </ContentWrapper>
      {archiveLoading && <CircularProgress />}
      {archiveError && (
        <Message type="error">Fout bij het laden van het archief van het schooljaar</Message>
      )}
      {archiveSuccess && archiveData.length > 0 && (
        <ContentWrapper className="desktop-50">
          <DashboardItem title={'Mijn archief'}>
            <ArchiveList archivedBooks={archiveData} />
          </DashboardItem>
        </ContentWrapper>
      )}
    </>
  );
};

export default StudentDashboardPage;
